/**
 * @fileoverview The gardener's own note shortcuts as a row of coloured pills, read from
 * their settings.
 *
 * Shared by the journal, the photo dialog and the bed card's note panel (the Add a to-do sheet now draws the job pills, JobChips), and it only reports which pill
 * was tapped: what a tap does is the caller's to decide, so the pill can look
 * and feel the same in both places.
 *
 * Last in the row, a dashed pill holding just a + (its aria-label says "Add a quick note") opens the gardener's quick notes in a
 * panel (NoteShortcutSettings, the same list as Profile > Labels) to add,
 * rename, recolour, reorder (drag the handle) or remove one right there; a new one shows in the row at
 * once. With none yet, the row is that one pill.
 *
 * Where: NotesSection, PhotoNoteField and BedActions. The pills are drawn by WordPills (B140), shared with JobChips.
 * API: GET /note-shortcuts (query ['noteShortcuts']).
 * Settings: the gardener's own shortcuts and colours (Profile);
 * NOTES_SHORTCUTS_STALE_MS (config/tunables/plantList).
 * Words (en/es): plants.notes.quickNotes.add (the + pill's aria-label), .title; the pills are the gardener's own words.
 *
 * @module QuickNoteChips
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { NoteShortcutSettings } from '@/user/components/NoteShortcutSettings';
import { WordPills } from '@/shared/components/WordPills';
import { getNoteShortcuts } from '@/shared/api/noteShortcuts';
import { NOTES_SHORTCUTS_STALE_MS } from '@/config/tunables/plantList';

/** What a tap on a pill reports to, and how the row sits. */
interface QuickNoteChipsProps {
  /** Given the pill's wording, which is the note text itself. */
  onPick: (label: string) => void;
  /** Greys the pills while the note they would go into is being saved. */
  disabled?: boolean;
  /** Extra classes for the row, for the caller's spacing. */
  className?: string;
}

/**
 * The gardener's tappable pills.
 *
 * One row, two callers, and deliberately no opinion about what a tap means:
 * beside the journal and beside a photo, each caller drops the word into its
 * own box so it can be added to before it is saved. The pill is the same
 * either way, which is the point — a gardener should not have to learn it
 * twice.
 *
 * @param props - QuickNoteChipsProps.
 */
export const QuickNoteChips: React.FC<QuickNoteChipsProps> = ({
  onPick, disabled = false, className,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** Whether the quick notes panel is open. */
  const [managing, setManaging] = React.useState(false);
  /** The gardener's shortcuts (GET /note-shortcuts), in their own order. */
  const { data: shortcuts = [] } = useQuery({
    queryKey: ['noteShortcuts'],
    queryFn: getNoteShortcuts,
    staleTime: NOTES_SHORTCUTS_STALE_MS,
  });

  return (
    <WordPills
      items={shortcuts.map((shortcut) => ({ key: String(shortcut.shortcut_id), label: shortcut.label, colorKey: shortcut.color_key }))}
      onPick={(item) => onPick(item.label)}
      disabled={disabled}
      className={className}
    >
      <Pill
        size="sm"
        tone="dashed"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setManaging(true);
        }}
        aria-label={t('plants.notes.quickNotes.add')}
        icon={<Plus />}
      />
      <BottomPanel open={managing} onOpenChange={setManaging} title={t('plants.notes.quickNotes.title')} className="max-h-[85dvh] overflow-y-auto">
        {/* Taps inside the panel stay in it, never reaching the card behind. */}
        <div onClick={(e) => e.stopPropagation()}>
          <NoteShortcutSettings inPanel />
        </div>
      </BottomPanel>
    </WordPills>
  );
};
